// In-memory, per-app-session coordination for optional nudges (survey,
// profile photo, Vibe Verified, store review). Not persisted on purpose: "one
// optional popup per session" means per app process.
//
// - activeMainTab: MainTabs' custom tab state (not a navigation route), so
//   the nudge host can stay off Chat/Plans.
// - overlays: critical or task UI currently on screen (the update prompt,
//   the survey itself, …). Optional nudges never appear on top of them.
// - sessionUsed: an optional popup already appeared (or the user opened the
//   survey/a reminder) this session.

type OpenSurveyListener = (options: { preview?: boolean }) => void;

let activeMainTab: string | null = null;
const overlays = new Set<string>();
let sessionUsed = false;
let openedFromReminderPush = false;
const openSurveyListeners = new Set<OpenSurveyListener>();
// A request made before the nudge host is mounted (e.g. a cold start from the
// feedback push) is held and delivered when it subscribes.
let pendingOpenSurvey: { preview?: boolean } | null = null;

export const nudgeSession = {
  setActiveMainTab(name: string | null) { activeMainTab = name; },
  getActiveMainTab() { return activeMainTab; },
  setOverlay(name: string, visible: boolean) {
    if (visible) overlays.add(name);
    else overlays.delete(name);
  },
  hasOverlay() { return overlays.size > 0; },
  markSessionUsed() { sessionUsed = true; },
  isSessionUsed() { return sessionUsed; },
  markOpenedFromReminderPush() {
    openedFromReminderPush = true;
    sessionUsed = true;
  },
  wasOpenedFromReminderPush() { return openedFromReminderPush; },
  /** Settings → "Give feedback". */
  /** preview (dev builds only): opens the real form without any Firestore reads/writes of survey state. */
  requestOpenSurvey(options: { preview?: boolean } = {}) {
    if (!openSurveyListeners.size) {
      pendingOpenSurvey = options;
      return;
    }
    openSurveyListeners.forEach(listener => listener(options));
  },
  onOpenSurveyRequested(listener: OpenSurveyListener) {
    openSurveyListeners.add(listener);
    if (pendingOpenSurvey) {
      const options = pendingOpenSurvey;
      pendingOpenSurvey = null;
      listener(options);
    }
    return () => { openSurveyListeners.delete(listener); };
  },
  /** Test helper. */
  resetForTests() {
    activeMainTab = null;
    overlays.clear();
    sessionUsed = false;
    openedFromReminderPush = false;
    pendingOpenSurvey = null;
  },
};

/** Tabs where a calm optional popup may appear (never Chat or Plans). */
export const CALM_MAIN_TABS = new Set(['Map', 'Discover', 'Feed', 'Profile']);
